import { Direction } from "odyssey-shared";
import type { KeyboardOwner } from "../../ui/input/router-types.ts";
import { StickController, drivesStick } from "./stick-controller.ts";
import "./stick.css";

/**
 * The stick's radius in logical pixels: half the base, which is 64 across.
 */
export const STICK_RADIUS_LOGICAL = 32;

/**
 * What the scene reads of the stick: the intent it sends now.
 */
export interface StickSource {
  /**
   * The direction the stick sends now; none while no finger holds it.
   *
   * @returns The direction.
   */
  direction(): Direction;
}

/**
 * What the stick needs from the page.
 */
export interface StickOptions {
  /**
   * Who owns the keyboard: the stick lets go whenever it is not the game (a focused field or a modal).
   */
  readonly owner: () => KeyboardOwner;
  /**
   * The width of a logical pixel in CSS pixels now.
   */
  readonly px: () => number;
  /**
   * Hears a short tap in the zone, in CSS pixels of the page, to hand to the world as a press (`GameScene.tapWorld`).
   */
  readonly tap?: (x: number, y: number) => void;
  /**
   * Whether the player is in the world (`GameScene.touchLive`): the stick shows only while it is, and lets go otherwise.
   */
  readonly live?: () => boolean;
}

/**
 * The mounted stick.
 */
export interface MountedStick extends StickSource {
  /**
   * Shows the stick on a touch page and hides it elsewhere; hiding lets go.
   *
   * @param on True to show.
   */
  show(on: boolean): void;
  /**
   * Lets go of the finger that holds the stick.
   */
  release(): void;
  /**
   * Removes the stick from the page.
   */
  destroy(): void;
}

/**
 * Mounts the floating stick (a short tap in its zone goes to the world as a press, and a mouse passes through it) as the first child of the overlay root, so every window and control lies above it. The
 * zone takes the pointer of a finger (not a mouse) on its own element, captured, so the finger drives the stick alone
 * and a second finger on a button or a window's title bar is that control's.
 *
 * @param root The overlay root.
 * @param options The keyboard's owner and the logical pixel's width.
 * @returns The stick, hidden until `show(true)`.
 */
export function mountStick(root: HTMLElement, options: StickOptions): MountedStick {
  const controller = new StickController();
  const zone = document.createElement("div");
  zone.id = "touch-stick";
  zone.hidden = true;
  const base = document.createElement("div");
  base.className = "stick-base";
  const knob = document.createElement("div");
  knob.className = "stick-knob";
  base.append(knob);
  zone.append(base);
  root.prepend(zone);

  /**
   * Draws the controller's view: the base where the finger landed and the knob at its offset, or the resting base.
   */
  const draw = (): void => {
    const view = controller.view();
    base.dataset.active = String(view.active);
    if (view.active) {
      const rect = zone.getBoundingClientRect();
      const half = STICK_RADIUS_LOGICAL * options.px();
      base.style.left = `${view.originX - rect.left - half}px`;
      base.style.top = `${view.originY - rect.top - half}px`;
      knob.style.transform = `translate(${view.knobX}px, ${view.knobY}px)`;
    } else {
      base.style.left = "";
      base.style.top = "";
      knob.style.transform = "";
    }
  };

  /**
   * Takes a finger as the stick's, when the keyboard is the game's and nothing holds the stick already.
   *
   * @param event The press.
   * @returns True when the finger is now the stick's.
   */
  const begin = (event: PointerEvent): boolean => {
    if (!drivesStick(event.pointerType) || options.owner() !== "game") {
      return false;
    }
    if (!controller.down(event.pointerId, event.clientX, event.clientY, STICK_RADIUS_LOGICAL * options.px(), event.timeStamp)) {
      return false;
    }
    zone.setPointerCapture(event.pointerId);
    event.preventDefault();
    draw();
    return true;
  };
  zone.addEventListener("pointerdown", begin);
  zone.addEventListener("pointermove", (event) => {
    controller.move(event.pointerId, event.clientX, event.clientY);
    draw();
  });
  zone.addEventListener("pointerup", (event) => {
    const tap = controller.up(event.pointerId, event.timeStamp);
    draw();
    if (tap !== null) {
      options.tap?.(tap.x, tap.y);
    }
  });
  /**
   * Lets go of the finger on a cancel or a lost capture; no tap comes of these.
   *
   * @param event The event.
   */
  const lift = (event: PointerEvent): void => {
    controller.up(event.pointerId);
    draw();
  };
  zone.addEventListener("pointercancel", lift);
  zone.addEventListener("lostpointercapture", lift);
  zone.addEventListener("contextmenu", (event) => event.preventDefault());
  /**
   * Lets go of whatever holds the stick, as a lost focus or a hidden stick does.
   */
  const blur = (): void => {
    controller.release();
    draw();
  };
  window.addEventListener("blur", blur);
  /**
   * Lets a mouse through the zone to the world while a mouse is the pointer in use, and takes fingers again once a finger
   * or a pen is: a mouse's moves and presses reach the window before the zone is asked again, so a finger's first
   * touch after a mouse, which the browser aims at the world beneath the pass-through zone, is taken as the stick's here
   * and kept from the world.
   *
   * @param event The pointer event the window heard.
   */
  const follow = (event: PointerEvent): void => {
    const wasMouse = zone.style.pointerEvents === "none";
    zone.style.pointerEvents = drivesStick(event.pointerType) ? "" : "none";
    if (wasMouse && event.type === "pointerdown" && drivesStick(event.pointerType) && !zone.hidden && !root.contains(event.target as Node | null) && insideZone(event)) {
      if (begin(event)) {
        event.stopPropagation();
      }
    }
  };
  /**
   * Whether a press lies inside the zone's box.
   *
   * @param event The press.
   * @returns True inside it.
   */
  const insideZone = (event: PointerEvent): boolean => {
    const rect = zone.getBoundingClientRect();
    return event.clientX >= rect.left && event.clientX < rect.right && event.clientY >= rect.top && event.clientY < rect.bottom;
  };
  window.addEventListener("pointermove", follow, true);
  window.addEventListener("pointerdown", follow, true);

  let wanted = false;
  let frame = 0;
  /**
   * Shows the zone while it is wanted and the player is in the world, and lets go when it hides.
   */
  const watch = (): void => {
    const visible = wanted && (options.live?.() ?? true);
    if (zone.hidden === visible) {
      zone.hidden = !visible;
      if (!visible) {
        blur();
      }
    }
    frame = requestAnimationFrame(watch);
  };

  return {
    direction(): Direction {
      if (options.owner() !== "game") {
        if (controller.view().active) {
          blur();
        }
        return Direction.None;
      }
      return controller.direction();
    },
    show(on: boolean): void {
      wanted = on;
      cancelAnimationFrame(frame);
      if (on) {
        watch();
      } else {
        zone.hidden = true;
        blur();
      }
    },
    release: blur,
    destroy(): void {
      cancelAnimationFrame(frame);
      window.removeEventListener("blur", blur);
      window.removeEventListener("pointermove", follow, true);
      window.removeEventListener("pointerdown", follow, true);
      zone.remove();
    },
  };
}
